import { useLazyContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { RedoDiscount } from "@redotech/redo-merchant-marketing-rpc/schema/discounts/discount-schemas";
import {
  DiscountGroupType,
  DiscountMinRequirementType,
  DiscountValueType,
  ExpirationType,
} from "@redotech/redo-model/discount";
import {
  BxgyTriggerType,
  DiscountPurchaseType,
  DiscountSettingsType,
  DynamicRangeExpirationUnits,
  type DiscountCategory,
} from "@redotech/redo-model/discount/discount-db-parser";
import { useEffect, useMemo, useState } from "react";
import { BadgeRedoListItem } from "../../arbiter-components/list/redo-list";
import { TimeOfDay } from "../../arbiter-components/time/redo-time-picker";
import type { ProductSelection, ShopifySegment } from "../discount-selector";
import {
  combineDateTime,
  CustomerEligibility,
  DiscountsContext,
  getInitialSegments,
  INITIAL_CODE_NAME,
  INITIAL_START_TIME,
  ProductsContext,
  ShopifySegmentsContext,
} from "../discount-selector";

export function useDiscountForm(
  existingDiscount?: RedoDiscount,
  category?: DiscountCategory,
) {
  const [discounts] = useLazyContext(DiscountsContext);
  const [productsContext] = useLazyContext(ProductsContext);
  const [shopifySegmentsLoad] = useLazyContext(ShopifySegmentsContext);
  const [discountName, setDiscountName] = useState(
    existingDiscount?.name || "",
  );

  const [codeGenerationStrategy, setCodeGenerationStrategy] =
    useState<DiscountGroupType>(() => {
      if (category === "RECOVER") {
        return DiscountGroupType.DYNAMIC;
      }
      return (
        existingDiscount?.codeGenerationStrategy.strategy ||
        DiscountGroupType.STATIC
      );
    });

  const [codeStrategyName, setCodeStrategyName] = useState(() => {
    if (!existingDiscount) {
      return INITIAL_CODE_NAME;
    }
    return existingDiscount.codeGenerationStrategy.code;
  });

  const [discountAmount, setDiscountAmount] = useState<number | undefined>(
    () => {
      if (
        existingDiscount?.discountSettings.settingsType ===
        DiscountSettingsType.SHOPIFY_BASIC
      ) {
        return existingDiscount.discountSettings.discountValueAmount;
      }
      return undefined;
    },
  );

  const [discountValueType, setDiscountValueType] = useState<DiscountValueType>(
    () => {
      if (
        existingDiscount?.discountSettings.settingsType ===
        DiscountSettingsType.SHOPIFY_BASIC
      ) {
        return existingDiscount.discountSettings.discountValueType;
      }
      return DiscountValueType.PERCENTAGE;
    },
  );

  // Combination settings
  const [combineDiscountWith, setCombineDiscountWith] = useState<{
    orderDiscounts: boolean;
    productDiscounts: boolean;
    shippingDiscount: boolean;
  }>(() => {
    if (!existingDiscount) {
      return {
        orderDiscounts: false,
        productDiscounts: false,
        shippingDiscount: false,
      };
    } else {
      const existingCombinesWith =
        existingDiscount.discountSettings.combinesWith;
      return {
        orderDiscounts: existingCombinesWith?.orderDiscounts || false,
        productDiscounts: existingCombinesWith?.productDiscounts || false,
        shippingDiscount: existingCombinesWith?.shippingDiscount || false,
      };
    }
  });

  // Item restrictions (only applicable to SHOPIFY_BASIC)
  const [excludeOnSale, setExcludeOnSale] = useState(() => {
    if (
      existingDiscount?.discountSettings.settingsType ===
      DiscountSettingsType.SHOPIFY_BASIC
    ) {
      return existingDiscount.discountSettings.excludeItemsOnSale || false;
    }
    return false;
  });

  const [specifyCollections, setSpecifyCollections] = useState(() => {
    if (
      existingDiscount?.discountSettings.settingsType ===
      DiscountSettingsType.SHOPIFY_BASIC
    ) {
      return (
        !!existingDiscount.discountSettings.appliesToCollections &&
        !!existingDiscount.discountSettings.appliesToCollections.length
      );
    }
    return false;
  });

  const [specifyProducts, setSpecifyProducts] = useState(() => {
    if (
      existingDiscount?.discountSettings.settingsType ===
      DiscountSettingsType.SHOPIFY_BASIC
    ) {
      return !!existingDiscount.discountSettings.appliesToProducts?.length;
    }
    return false;
  });

  const [selectedProducts, setSelectedProducts] = useState<ProductSelection[]>(
    () => {
      if (
        existingDiscount?.discountSettings.settingsType !==
        DiscountSettingsType.SHOPIFY_BASIC
      ) {
        return [];
      }
      if (!existingDiscount.discountSettings.appliesToProducts) {
        return [];
      }

      return existingDiscount.discountSettings.appliesToProducts.map(
        (id: string) => {
          const productFromContext = productsContext.value?.find(
            (p) => p.id === id,
          );
          return {
            id,
            title:
              productFromContext?.name ||
              `Product ${id.includes("gid://shopify/Product/") ? id.split("/").pop() : id}`,
          };
        },
      );
    },
  );

  const [collectionIds, setCollectionIds] = useState<string[]>(() => {
    if (
      existingDiscount?.discountSettings.settingsType ===
      DiscountSettingsType.SHOPIFY_BASIC
    ) {
      return existingDiscount.discountSettings.appliesToCollections || [];
    }
    return [];
  });

  // Expiration
  const [expiration, setExpiration] = useState<RedoDiscount["expiration"]>(
    () => {
      if (category === "RECOVER") {
        return { expirationType: ExpirationType.EXPIRATION_DAYS, days: 10 };
      }
      return (
        existingDiscount?.expiration || { expirationType: ExpirationType.NEVER }
      );
    },
  );

  // Minimum requirements (supported by SHOPIFY_BASIC and SHOPIFY_FREE_SHIPPING, not BXGY)
  const [minimumQuantity, setMinimumQuantity] = useState<number | undefined>(
    () => {
      const settings = existingDiscount?.discountSettings;
      if (
        (settings?.settingsType === DiscountSettingsType.SHOPIFY_BASIC ||
          settings?.settingsType ===
            DiscountSettingsType.SHOPIFY_FREE_SHIPPING) &&
        settings.minimumRequirement?.type ===
          DiscountMinRequirementType.MINIMUM_QUANTITY
      ) {
        return settings.minimumRequirement.minimum;
      }
      return undefined;
    },
  );

  const [minimumSubtotal, setMinimumSubtotal] = useState<number | undefined>(
    () => {
      const settings = existingDiscount?.discountSettings;
      if (
        (settings?.settingsType === DiscountSettingsType.SHOPIFY_BASIC ||
          settings?.settingsType ===
            DiscountSettingsType.SHOPIFY_FREE_SHIPPING) &&
        settings.minimumRequirement?.type ===
          DiscountMinRequirementType.MINIMUM_SUBTOTAL
      ) {
        return settings.minimumRequirement.minimum;
      }
      return undefined;
    },
  );

  // Customer eligibility
  const [customerEligibility, setCustomerEligibility] =
    useState<CustomerEligibility>(() => {
      if (!existingDiscount) {
        return CustomerEligibility.ALL;
      }

      if (existingDiscount.discountSettings.limitToCustomers?.length) {
        return CustomerEligibility.SPECIFIC;
      }
      if (existingDiscount.discountSettings.limitToSegments?.length) {
        return CustomerEligibility.SEGMENTS;
      }

      return CustomerEligibility.ALL;
    });

  const [selectedCustomerIdsString, setSelectedCustomerIdsString] =
    useState<string>(
      existingDiscount?.discountSettings.limitToCustomers?.join(", ") || "",
    );

  // Convert string to array for validation
  const selectedCustomerIds = useMemo(() => {
    return selectedCustomerIdsString
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);
  }, [selectedCustomerIdsString]);

  // Usage limits
  const [totalUsageLimit, setTotalUsageLimit] = useState<number | undefined>(
    existingDiscount?.discountSettings.totalUsageLimit || undefined,
  );

  const [appliesOncePerCustomer, setAppliesOncePerCustomer] = useState<boolean>(
    () => {
      if (category === "RECOVER") {
        return true;
      }
      return existingDiscount?.discountSettings.appliesOncePerCustomer || false;
    },
  );

  const [purchaseType, setPurchaseType] = useState<
    DiscountPurchaseType | undefined
  >(() => {
    const settings = existingDiscount?.discountSettings;
    if (
      settings?.settingsType === DiscountSettingsType.SHOPIFY_BASIC ||
      settings?.settingsType === DiscountSettingsType.SHOPIFY_FREE_SHIPPING
    ) {
      return settings.purchaseType;
    }
    return undefined;
  });

  const [recurringCycleLimit, setRecurringCycleLimit] = useState<
    number | undefined
  >(() => {
    const settings = existingDiscount?.discountSettings;
    if (
      settings?.settingsType === DiscountSettingsType.SHOPIFY_BASIC ||
      settings?.settingsType === DiscountSettingsType.SHOPIFY_FREE_SHIPPING
    ) {
      return settings.recurringCycleLimit;
    }
    return undefined;
  });

  // Discount settings type (Basic, Free Shipping, BxGy)
  const [discountSettingsType, setDiscountSettingsType] =
    useState<DiscountSettingsType>(() => {
      return (
        existingDiscount?.discountSettings.settingsType ||
        DiscountSettingsType.SHOPIFY_BASIC
      );
    });

  // Free Shipping specific fields
  const [maximumShippingPrice, setMaximumShippingPrice] = useState<
    number | undefined
  >(() => {
    if (
      existingDiscount?.discountSettings.settingsType ===
      DiscountSettingsType.SHOPIFY_FREE_SHIPPING
    ) {
      return existingDiscount.discountSettings.maximumShippingPrice;
    }
    return undefined;
  });

  const [freeShippingCountryCodes, setFreeShippingCountryCodes] = useState<
    string[]
  >(() => {
    if (
      existingDiscount?.discountSettings.settingsType ===
      DiscountSettingsType.SHOPIFY_FREE_SHIPPING
    ) {
      return existingDiscount.discountSettings.countryCodes || [];
    }
    return [];
  });

  // BxGy specific fields - Customer Buys
  const [bxgyTriggerType, setBxgyTriggerType] = useState<BxgyTriggerType>(
    () => {
      if (
        existingDiscount?.discountSettings.settingsType ===
        DiscountSettingsType.SHOPIFY_BXGY
      ) {
        return existingDiscount.discountSettings.customerBuys.triggerType;
      }
      return BxgyTriggerType.MINIMUM_QUANTITY;
    },
  );

  const [bxgyTriggerQuantity, setBxgyTriggerQuantity] = useState<
    number | undefined
  >(() => {
    if (
      existingDiscount?.discountSettings.settingsType ===
      DiscountSettingsType.SHOPIFY_BXGY
    ) {
      return existingDiscount.discountSettings.customerBuys.quantity;
    }
    return 1;
  });

  const [bxgyTriggerAmount, setBxgyTriggerAmount] = useState<
    number | undefined
  >(() => {
    if (
      existingDiscount?.discountSettings.settingsType ===
      DiscountSettingsType.SHOPIFY_BXGY
    ) {
      return existingDiscount.discountSettings.customerBuys.amount;
    }
    return undefined;
  });

  const [bxgyTriggerItemType, setBxgyTriggerItemType] = useState<
    "products" | "collections"
  >(() => {
    if (
      existingDiscount?.discountSettings.settingsType ===
      DiscountSettingsType.SHOPIFY_BXGY
    ) {
      const hasCollections =
        (existingDiscount.discountSettings.customerBuys.collections?.length ??
          0) > 0;
      return hasCollections ? "collections" : "products";
    }
    return "products";
  });

  const [bxgyTriggerProducts, setBxgyTriggerProducts] = useState<
    ProductSelection[]
  >(() => {
    if (
      existingDiscount?.discountSettings.settingsType ===
      DiscountSettingsType.SHOPIFY_BXGY
    ) {
      return (
        existingDiscount.discountSettings.customerBuys.products?.map((p) => ({
          id: p.id,
          title: p.title || p.id,
        })) || []
      );
    }
    return [];
  });

  const [bxgyTriggerCollections, setBxgyTriggerCollections] = useState<
    string[]
  >(() => {
    if (
      existingDiscount?.discountSettings.settingsType ===
      DiscountSettingsType.SHOPIFY_BXGY
    ) {
      return (
        existingDiscount.discountSettings.customerBuys.collections?.map(
          (c) => c.id,
        ) || []
      );
    }
    return [];
  });

  // BxGy specific fields - Customer Gets
  const [bxgyRewardItemType, setBxgyRewardItemType] = useState<
    "products" | "collections"
  >(() => {
    if (
      existingDiscount?.discountSettings.settingsType ===
      DiscountSettingsType.SHOPIFY_BXGY
    ) {
      const hasCollections =
        (existingDiscount.discountSettings.customerGets.collections?.length ??
          0) > 0;
      return hasCollections ? "collections" : "products";
    }
    return "products";
  });

  const [bxgyRewardProducts, setBxgyRewardProducts] = useState<
    ProductSelection[]
  >(() => {
    if (
      existingDiscount?.discountSettings.settingsType ===
      DiscountSettingsType.SHOPIFY_BXGY
    ) {
      return (
        existingDiscount.discountSettings.customerGets.products?.map((p) => ({
          id: p.id,
          title: p.title || p.id,
        })) || []
      );
    }
    return [];
  });

  const [bxgyRewardCollections, setBxgyRewardCollections] = useState<string[]>(
    () => {
      if (
        existingDiscount?.discountSettings.settingsType ===
        DiscountSettingsType.SHOPIFY_BXGY
      ) {
        return (
          existingDiscount.discountSettings.customerGets.collections?.map(
            (c) => c.id,
          ) || []
        );
      }
      return [];
    },
  );

  const [bxgyRewardQuantity, setBxgyRewardQuantity] = useState<number>(() => {
    if (
      existingDiscount?.discountSettings.settingsType ===
      DiscountSettingsType.SHOPIFY_BXGY
    ) {
      return existingDiscount.discountSettings.customerGets.quantity;
    }
    return 1;
  });

  const [bxgyRewardDiscountType, setBxgyRewardDiscountType] =
    useState<DiscountValueType>(() => {
      if (
        existingDiscount?.discountSettings.settingsType ===
        DiscountSettingsType.SHOPIFY_BXGY
      ) {
        return existingDiscount.discountSettings.customerGets.discountValueType;
      }
      return DiscountValueType.PERCENTAGE;
    });

  const [bxgyRewardDiscountValue, setBxgyRewardDiscountValue] =
    useState<number>(() => {
      if (
        existingDiscount?.discountSettings.settingsType ===
        DiscountSettingsType.SHOPIFY_BXGY
      ) {
        return existingDiscount.discountSettings.customerGets.discountValue;
      }
      return 100; // Default to 100% off (free)
    });

  const [bxgyUsesPerOrderLimit, setBxgyUsesPerOrderLimit] = useState<
    number | undefined
  >(() => {
    if (
      existingDiscount?.discountSettings.settingsType ===
      DiscountSettingsType.SHOPIFY_BXGY
    ) {
      return existingDiscount.discountSettings.usesPerOrderLimit;
    }
    return undefined;
  });

  // Dynamic Range specific fields
  const [dynamicRangeMinPercentage, setDynamicRangeMinPercentage] = useState<
    number | undefined
  >(() => {
    if (
      existingDiscount?.discountSettings.settingsType ===
      DiscountSettingsType.DYNAMIC_RANGE
    ) {
      return existingDiscount.discountSettings.minPercentage;
    }
    return undefined;
  });

  const [dynamicRangeMaxPercentage, setDynamicRangeMaxPercentage] = useState<
    number | undefined
  >(() => {
    if (
      existingDiscount?.discountSettings.settingsType ===
      DiscountSettingsType.DYNAMIC_RANGE
    ) {
      return existingDiscount.discountSettings.maxPercentage;
    }
    return undefined;
  });

  const [dynamicRangeExpirationUnits, setDynamicRangeExpirationUnits] =
    useState<DynamicRangeExpirationUnits | undefined>(() => {
      if (
        existingDiscount?.discountSettings.settingsType ===
        DiscountSettingsType.DYNAMIC_RANGE
      ) {
        return existingDiscount.discountSettings.expiration.units;
      }
      return DynamicRangeExpirationUnits.DAYS;
    });

  const [dynamicRangeExpirationValue, setDynamicRangeExpirationValue] =
    useState<number | undefined>(() => {
      if (
        existingDiscount?.discountSettings.settingsType ===
        DiscountSettingsType.DYNAMIC_RANGE
      ) {
        return existingDiscount.discountSettings.expiration.value;
      }
      return 1;
    });

  // Shopify segments
  const [userSelectedShopifySegments, setUserSelectedShopifySegments] =
    useState<ShopifySegment[]>(() =>
      getInitialSegments(existingDiscount, shopifySegmentsLoad.value),
    );

  const [segmentsInitialized, setSegmentsInitialized] = useState(false);

  // Date/time
  const [startDate, setStartDate] = useState<Date | undefined>(
    existingDiscount?.discountSettings.startsAt ?? undefined,
  );

  const [startTime, setStartTime] = useState<TimeOfDay | null>(() => {
    if (existingDiscount?.discountSettings.startsAt) {
      const date = existingDiscount.discountSettings.startsAt;
      return { hours: date.getHours(), minutes: date.getMinutes() };
    }
    return INITIAL_START_TIME;
  });

  // Effects
  useEffect(() => {
    if (
      existingDiscount?.discountSettings.limitToSegments &&
      shopifySegmentsLoad.value &&
      !segmentsInitialized
    ) {
      const initialSegments = getInitialSegments(
        existingDiscount,
        shopifySegmentsLoad.value,
      );
      setUserSelectedShopifySegments(initialSegments);
      setSegmentsInitialized(true);
    }
  }, [existingDiscount, shopifySegmentsLoad.value, segmentsInitialized]);

  useEffect(() => {
    if (
      codeGenerationStrategy === DiscountGroupType.STATIC &&
      [ExpirationType.EXPIRATION_DAYS].includes(expiration.expirationType)
    ) {
      setExpiration({ expirationType: ExpirationType.NEVER });
    }
  }, [codeGenerationStrategy, expiration.expirationType]);

  useEffect(() => {
    if (discountSettingsType === DiscountSettingsType.DYNAMIC_RANGE) {
      setCodeGenerationStrategy(DiscountGroupType.DYNAMIC);
    }
  }, [discountSettingsType]);

  // Computed values
  const productIds = useMemo(
    () => selectedProducts.map((p) => p.id),
    [selectedProducts],
  );

  const fullStartDateTime = useMemo(
    () => combineDateTime(startDate, startTime),
    [startDate, startTime],
  );

  const ShopifySegmentOptions: BadgeRedoListItem<ShopifySegment>[] =
    useMemo(() => {
      if (!shopifySegmentsLoad.value) {
        return [];
      }
      return shopifySegmentsLoad.value.map((segment) => ({
        id: segment.id,
        value: segment,
        type: "badge" as const,
        badge: { text: segment.name },
      }));
    }, [shopifySegmentsLoad.value]);

  const selectedShopifySegmentOptions: BadgeRedoListItem<ShopifySegment>[] =
    useMemo(() => {
      return userSelectedShopifySegments.map((segment) => ({
        id: segment.id,
        value: segment,
        type: "badge" as const,
        badge: { text: segment.name },
      }));
    }, [userSelectedShopifySegments]);

  // Handlers
  const handleSetExcludeOnSale = useHandler((checked: boolean) => {
    setExcludeOnSale(checked);
    if (checked) {
      setSpecifyCollections(false);
      setSpecifyProducts(false);
    }
  });

  const handleSetSpecifyCollections = useHandler((checked: boolean) => {
    setSpecifyCollections(checked);
    if (checked) {
      setExcludeOnSale(false);
      setSpecifyProducts(false);
      // Clear products when switching to collections
      setSelectedProducts([]);
    }
  });

  const handleSetSpecifyProducts = useHandler((checked: boolean) => {
    setSpecifyProducts(checked);
    if (checked) {
      setExcludeOnSale(false);
      setSpecifyCollections(false);
      // Clear collections when switching to products
      setCollectionIds([]);
    }
  });

  const handleShopifySegmentChange = useHandler(
    (items: BadgeRedoListItem<ShopifySegment>[]) => {
      setUserSelectedShopifySegments(items.map((item) => item.value));
    },
  );

  const handleSetMinimumQuantity = useHandler((value: number | undefined) => {
    setMinimumQuantity(value);
    if (value && value > 0) {
      // Clear minimum subtotal when setting quantity
      setMinimumSubtotal(undefined);
    }
  });

  const handleSetMinimumSubtotal = useHandler((value: number | undefined) => {
    setMinimumSubtotal(value);
    if (value && value > 0) {
      // Clear minimum quantity when setting subtotal
      setMinimumQuantity(undefined);
    }
  });

  return {
    // State values
    discountName,
    setDiscountName,
    codeGenerationStrategy,
    setCodeGenerationStrategy,
    codeStrategyName,
    setCodeStrategyName,
    discountAmount,
    setDiscountAmount,
    discountValueType,
    setDiscountValueType,
    combineDiscountWith,
    setCombineDiscountWith,
    excludeOnSale,
    setExcludeOnSale,
    specifyCollections,
    setSpecifyCollections,
    specifyProducts,
    setSpecifyProducts,
    selectedProducts,
    setSelectedProducts,
    collectionIds,
    setCollectionIds,
    expiration,
    setExpiration,
    minimumQuantity,
    setMinimumQuantity,
    minimumSubtotal,
    setMinimumSubtotal,
    customerEligibility,
    setCustomerEligibility,
    selectedCustomerIds,
    selectedCustomerIdsString,
    setSelectedCustomerIdsString,
    totalUsageLimit,
    setTotalUsageLimit,
    appliesOncePerCustomer,
    setAppliesOncePerCustomer,
    purchaseType,
    setPurchaseType,
    recurringCycleLimit,
    setRecurringCycleLimit,
    userSelectedShopifySegments,
    setUserSelectedShopifySegments,
    startDate,
    setStartDate,
    startTime,
    setStartTime,

    // Discount settings type
    discountSettingsType,
    setDiscountSettingsType,

    // Free Shipping fields
    maximumShippingPrice,
    setMaximumShippingPrice,
    freeShippingCountryCodes,
    setFreeShippingCountryCodes,

    // BxGy fields - Customer Buys
    bxgyTriggerType,
    setBxgyTriggerType,
    bxgyTriggerQuantity,
    setBxgyTriggerQuantity,
    bxgyTriggerAmount,
    setBxgyTriggerAmount,
    bxgyTriggerItemType,
    setBxgyTriggerItemType,
    bxgyTriggerProducts,
    setBxgyTriggerProducts,
    bxgyTriggerCollections,
    setBxgyTriggerCollections,

    // BxGy fields - Customer Gets
    bxgyRewardItemType,
    setBxgyRewardItemType,
    bxgyRewardProducts,
    setBxgyRewardProducts,
    bxgyRewardCollections,
    setBxgyRewardCollections,
    bxgyRewardQuantity,
    setBxgyRewardQuantity,
    bxgyRewardDiscountType,
    setBxgyRewardDiscountType,
    bxgyRewardDiscountValue,
    setBxgyRewardDiscountValue,
    bxgyUsesPerOrderLimit,
    setBxgyUsesPerOrderLimit,

    // Dynamic Range fields
    dynamicRangeMinPercentage,
    setDynamicRangeMinPercentage,
    dynamicRangeMaxPercentage,
    setDynamicRangeMaxPercentage,
    dynamicRangeExpirationUnits,
    setDynamicRangeExpirationUnits,
    dynamicRangeExpirationValue,
    setDynamicRangeExpirationValue,

    // Computed values
    productIds,
    fullStartDateTime,
    ShopifySegmentOptions,
    selectedShopifySegmentOptions,

    // Handlers
    handleSetExcludeOnSale,
    handleSetSpecifyCollections,
    handleSetSpecifyProducts,
    handleShopifySegmentChange,
    handleSetMinimumQuantity,
    handleSetMinimumSubtotal,

    // Context data
    discounts,

    // Constants for validation
    CustomerEligibility,
    DiscountSettingsType,
    BxgyTriggerType,
  };
}
